import React from 'react';
import { Link } from 'react-router-dom';
import './PersonalPrizePrivacyPage.css';

const PersonalPrizeTermsPage: React.FC = () => {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <section className="privacy-hero">
          <div className="privacy-hero-row">
            <div>
              <h1>Terms of Service</h1>
              <div className="privacy-meta">Personal Prize — Terms of Service</div>
            </div>
            <div className="privacy-hero-right">
              <div className="privacy-badge">Version 1.0</div>
              <div className="privacy-updated">Last Updated: March 23, 2026</div>
            </div>
          </div>
        </section>

        <article className="privacy-card">
          <h2>Terms of Service</h2>
          <p>
            These Terms of Service (&quot;Terms&quot;) govern your use of the Personal Prize mobile and web
            application (&quot;App&quot;) provided by SynorLab (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;).
            By downloading, installing, or using the App, you agree to these Terms. If you do not agree,
            do not use the App.
          </p>
          <p>
            Please also read our{' '}
            <Link to="/personal-prize/privacy">Privacy Policy</Link>, which explains how we handle your data.
          </p>

          <h2>1. Description of Service</h2>
          <p>
            Personal Prize is a personalized gacha and reward application that allows you to create
            custom prize pools, message cards, and interactive draw experiences for yourself, your
            partner, or your family. The App stores data locally on your device and does not require
            account registration.
          </p>
          <p>
            Features may include custom prize pool management, personal message cards, various draw
            animations (roulette, scratch-off, flip-card, and more), optional sharing via links,
            data export/import, and display of advertisements through Google AdMob.
          </p>

          <h2>2. License and Acceptable Use</h2>
          <p>
            We grant you a limited, non-exclusive, non-transferable, revocable license to use the App for
            personal, non-commercial purposes, subject to these Terms and applicable app store rules.
          </p>
          <p>You agree not to:</p>
          <ul>
            <li>Use the App for any unlawful purpose or in violation of any applicable laws</li>
            <li>Reverse engineer, decompile, or attempt to extract the source code of the App</li>
            <li>Interfere with or disrupt the App&apos;s functionality or security features</li>
            <li>Use the App to store, share, or distribute content that is illegal, harmful, or infringes third-party rights</li>
            <li>Remove or alter any proprietary notices in the App</li>
          </ul>

          <h2>3. Prize Pools and Sharing</h2>
          <p>
            <strong>Users are solely responsible for the content they create and share through the App.</strong>
          </p>
          <p>
            When you create prize pools, message cards, or other content, you are responsible for ensuring
            that the content is appropriate and that you have the right to use any images or text you include.
          </p>
          <p>
            If you choose to share a prize pool via link, you control who receives access. Share links use
            anonymous identifiers and do not expose your identity. However, any personal information you
            include in message cards or prize descriptions will be visible to those you share with.
          </p>
          <p>
            We are not responsible for how recipients use shared prize pools or for content you choose to share.
          </p>

          <h2>4. Export and Import of Data</h2>
          <p>
            The App may allow you to export or import prize pool data files. If you choose to export your
            data, the resulting file goes <strong>directly to a destination you select</strong>:
          </p>
          <ul>
            <li>Local storage on your device</li>
            <li>Cloud drives you control (Google Drive, iCloud, Dropbox, etc.)</li>
            <li>Any other location you choose</li>
          </ul>
          <p>
            <strong>Export Responsibility:</strong> Personal Prize never touches that file once it leaves
            the app. <strong>Users are responsible for securely storing, transferring, and managing exported files.</strong>{' '}
            We are not responsible for data loss, unauthorized access, or exposure resulting from exported
            files stored outside the App.
          </p>
          <p>
            <strong>Import Risk:</strong> Importing files from untrusted sources may pose security risks.
            Users should only import files they trust.
          </p>

          <h2>5. Data Loss and Backup</h2>
          <p>
            <strong>We are not responsible for data loss resulting from device failure, deletion, software errors, or user actions.</strong>
          </p>
          <p>Data loss may occur due to:</p>
          <ul>
            <li>Device malfunction or hardware failure</li>
            <li>Accidental deletion by the user</li>
            <li>App uninstallation</li>
            <li>Operating system updates or changes</li>
            <li>Loss or theft of the device</li>
            <li>Software errors or bugs</li>
          </ul>
          <p>
            <strong>Backup Recommendation:</strong> Users are strongly encouraged to maintain secure
            backups of important exported data. We do not maintain servers for data backup and cannot
            recover lost data.
          </p>

          <h2>6. Security Disclaimer</h2>
          <p>
            While we strive to use commercially acceptable means to protect your information, we cannot
            guarantee its absolute security. No method of storage or transmission can be guaranteed to
            be 100% secure. You use the App at your own risk.
          </p>

          <h2>7. Advertising</h2>
          <p>
            The App displays advertisements through Google AdMob. Ad delivery and related data practices
            are governed by Google&apos;s policies. Disabling personalized ads through your device settings
            or <a href="https://adssettings.google.com" target="_blank" rel="noopener noreferrer">Google ad preferences</a>{' '}
            does not affect core App functionality. See our{' '}
            <Link to="/personal-prize/privacy">Privacy Policy</Link> for details on advertising and data.
          </p>

          <h2>8. Intellectual Property</h2>
          <p>
            The App, including its design, code, graphics, and branding, is owned by SynorLab and
            protected by intellectual property laws. Your content — prize pools, message cards, images,
            and other data you create — remains yours. We claim no ownership over your in-app content.
          </p>

          <h2>9. Disclaimer of Warranties</h2>
          <p>
            THE APP IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE&quot; WITHOUT WARRANTIES OF ANY KIND,
            WHETHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY,
            FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE APP WILL
            BE UNINTERRUPTED, ERROR-FREE, OR FREE OF HARMFUL COMPONENTS.
          </p>

          <h2>10. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, SYNORLAB AND ITS DEVELOPERS SHALL NOT BE
            LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS
            OF DATA, PROFITS, OR GOODWILL, ARISING FROM YOUR USE OF OR INABILITY TO USE THE APP, EVEN IF
            WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          </p>
          <p>
            Our total liability for any claim arising from these Terms or your use of the App shall not
            exceed the amount you paid for the App in the twelve (12) months preceding the claim, or
            zero if the App was provided free of charge.
          </p>

          <h2>11. Updates and Changes</h2>
          <p>
            We may update the App or these Terms from time to time. Continued use of the App after changes
            take effect constitutes acceptance of the revised Terms. We will update the &quot;Last Updated&quot;
            date when changes are made. Material changes may also be communicated through the App or
            app store listing.
          </p>

          <h2>12. Termination</h2>
          <p>
            You may stop using the App at any time by uninstalling it from your device. We may suspend
            or discontinue the App or your access to it if you violate these Terms, though we have no
            obligation to monitor your use.
          </p>

          <h2>13. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with applicable laws, without
            regard to conflict-of-law principles. Any disputes shall be resolved in the courts of
            competent jurisdiction.
          </p>

          <h2>14. Contact Us</h2>
          <div className="privacy-contact">
            <div className="privacy-email">
              📧 <a href="mailto:synorlab.com@gmail.com">synorlab.com@gmail.com</a>
            </div>
            <div className="privacy-contact-note">We aim to respond within a reasonable time.</div>
          </div>

          <footer className="privacy-footer">
            <div>© Personal Prize — All rights reserved.</div>
          </footer>
        </article>

        <div className="privacy-back">
          <Link to="/personal-prize" className="privacy-back-link">← Back to Personal Prize</Link>
        </div>
      </div>
    </div>
  );
};

export default PersonalPrizeTermsPage;
